import * as Sentry from '@sentry/react';

/**
 * Tag the current Sentry scope with the sub-app name.
 *
 * Sentry is already initialised globally by SentryAsset (PHP layer) via the
 * CDN bundle. This function must NOT call Sentry.init() again — doing so would
 * create a second SDK instance and drop the global error handlers set up by the
 * PHP layer.
 *
 * The tag appears in Sentry as `app: toast | slider | main` and can be used to
 * filter issues to a specific React bundle.
 *
 * @param app  Short label for the React sub-app ('toast' | 'slider' | 'main')
 */
export function tagSentryApp(app: 'toast' | 'slider' | 'main'): void {
  Sentry.setTag('app', app);
}

declare global {
  interface Window {
    Sentry?: { captureException?: (error: unknown, context?: Record<string, unknown>) => unknown };
  }
}

/**
 * Report an error an error boundary caught to Sentry.
 *
 * React does not rethrow errors a boundary caught, so the CDN SDK's global
 * handlers never see them. The CDN instance (window.Sentry, initialised by
 * SentryAsset) is the configured one; the npm package is not initialised.
 */
export function captureBoundaryError(error: unknown, componentStack?: string | null): void {
  try {
    window.Sentry?.captureException?.(error, {
      tags: { errorBoundary: 'true' },
      contexts: { react: { componentStack: componentStack ?? '' } },
    });
  } catch {
    // reporting must never break the fallback
  }
}
